import { Bell, BellOff, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { isPushSupported, usePushSubscription } from "@/hooks/usePushSubscription";

function UnsupportedBanner() {
  return (
    <div className="rounded-2xl border border-border bg-muted/40 p-4 text-sm text-stone-600" data-testid="push-unsupported">
      This browser doesn't support push notifications. Install the app to your home screen for the full experience.
    </div>
  );
}

function BlockedBanner() {
  return (
    <div className="rounded-2xl border border-border bg-muted/40 p-4 flex items-start gap-3" data-testid="push-blocked">
      <BellOff className="w-5 h-5 text-stone-500 shrink-0 mt-0.5" />
      <div className="text-sm text-stone-700">
        Notifications are blocked in browser settings. Re-enable them from the padlock icon in your URL bar to get passport &amp; check-in reminders.
      </div>
    </div>
  );
}

function EnabledBanner({ status, onTest, onDisable }) {
  return (
    <div className="rounded-2xl border border-border bg-primary/5 p-4 flex flex-wrap items-center gap-3" data-testid="push-enabled">
      <div className="w-9 h-9 rounded-xl bg-primary text-primary-foreground grid place-items-center shrink-0">
        <Bell className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-[220px]">
        <div className="font-display text-sm">Reminders on for this device</div>
        <div className="text-xs text-stone-600">Passport (30d, 7d) &amp; check-in (24h, 2h) alerts are active.</div>
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" variant="outline" className="rounded-full" onClick={onTest} data-testid="push-test-btn">
          Send test
        </Button>
        <Button
          size="sm" variant="ghost"
          className="rounded-full text-stone-600"
          onClick={onDisable}
          disabled={status === "disabling"}
          data-testid="push-disable-btn"
        >
          {status === "disabling" ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          Turn off
        </Button>
      </div>
    </div>
  );
}

function EnableCta({ status, onEnable }) {
  const busy = status === "enabling";
  return (
    <div className="rounded-2xl border border-border bg-muted/40 p-4 flex flex-wrap items-center gap-3" data-testid="push-cta">
      <div className="w-9 h-9 rounded-xl bg-stone-900 text-white grid place-items-center shrink-0">
        <Bell className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-[220px]">
        <div className="font-display text-sm">Never miss a check-in</div>
        <div className="text-xs text-stone-600">
          Get a nudge 30 &amp; 7 days before passports expire, and 24h &amp; 2h before every check-in.
        </div>
      </div>
      <Button
        size="sm"
        className="rounded-full bg-primary text-primary-foreground"
        onClick={onEnable}
        disabled={busy}
        data-testid="push-enable-btn"
      >
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}
        Enable reminders
      </Button>
    </div>
  );
}

export default function EnableNotifications() {
  const { status, permission, enabled, enable, disable, sendTest } = usePushSubscription();

  if (!isPushSupported()) return <UnsupportedBanner />;
  if (permission === "denied") return <BlockedBanner />;
  if (enabled) return <EnabledBanner status={status} onTest={sendTest} onDisable={disable} />;
  return <EnableCta status={status} onEnable={enable} />;
}
